import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronUp,
	faChevronDown,
	faWrench,
	faUserAstronaut,
	faDolly,
	faTimes,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { round100 } from "../../../utilities/Addons"
import CommandeHeader from "./CommandeHeader"
import AnnulationCommandeVirtuelleModalV2 from "./AnnulationCommandeVirtuelleModalV2"

// Prix : toujours 2 décimales (laisse la valeur telle quelle si non numérique).
const fmt2 = (v) => {
	const n = Number(v)
	return Number.isNaN(n) ? v : n.toFixed(2)
}

// Carte d'une commande de stock virtuel restant. En-tête (fournisseur, date,
// référence, n°) + boutons "transfert stock" (chef, si onTransfert fourni) et
// "commande perso" + tableau d'articles repliable, chaque article ayant une
// action de consommation (clé).
// GE-79 (Jordan 30.10) : tous les boutons d'action sont ici — ✕ « Annuler la
// commande » dans l'en-tête, ✕ par ligne dans les outils (grisé, raison au
// survol, si la ligne est engagée). `onAnnule` : refetch après succès.
const StockVirtuelCard = ({
	commande,
	onConso,
	onCmdPerso,
	onTransfert,
	onAnnule,
}) => {
	const [open, setOpen] = useState(true)
	// null | { article } (article null = la commande)
	const [annulation, setAnnulation] = useState(null)
	const articles = commande.articles || []
	const total = round100(
		articles.reduce((s, a) => s + a.quantite_recue * a.prix_achat_ht, 0),
	)

	return (
		<div style={cs.card}>
			<CommandeHeader
				fournisseur={commande.fournisseur_nom}
				date={commande.date_commande}
				reference={commande.reference}
				numero={commande.numero_commande}
				total={total}
				actions={
					<>
						{onTransfert && (
							<button
								type="button"
								style={gs.iconBtn(COLORS.textMuted)}
								onClick={onTransfert}
								title="Transférer dans le stock"
							>
								<FontAwesomeIcon icon={faDolly} />
							</button>
						)}
						{/* Perso seulement si rien n'est engagé : même règle
						    que l'annulation (Jordan 31.10, garde serveur). */}
						<button
							type="button"
							style={
								commande.annulable
									? gs.iconBtn(COLORS.textMuted)
									: {
											...gs.iconBtn(COLORS.textMuted),
											...cs.iconOff,
										}
							}
							onClick={onCmdPerso}
							disabled={!commande.annulable}
							aria-label="Commande personnelle"
							title={
								commande.annulable
									? "Commande personnelle"
									: commande.raison_non_annulable
							}
						>
							<FontAwesomeIcon icon={faUserAstronaut} />
						</button>
						<button
							type="button"
							style={
								commande.annulable
									? cs.iconDanger
									: { ...cs.iconDanger, ...cs.iconOff }
							}
							disabled={!commande.annulable}
							aria-label="Annuler la commande"
							title={
								commande.annulable
									? "Annuler la commande"
									: commande.raison_non_annulable
							}
							onClick={() => setAnnulation({ article: null })}
						>
							<FontAwesomeIcon icon={faTimes} />
						</button>
						<button
							type="button"
							style={gs.iconBtn(COLORS.textMuted)}
							onClick={() => setOpen((o) => !o)}
							title={open ? "Replier" : "Déplier"}
						>
							<FontAwesomeIcon
								icon={open ? faChevronUp : faChevronDown}
							/>
						</button>
					</>
				}
			/>

			{annulation && (
				<AnnulationCommandeVirtuelleModalV2
					commande={commande}
					article={annulation.article}
					onClose={() => setAnnulation(null)}
					onDone={onAnnule}
				/>
			)}

			{open && (
				<div style={cs.tableWrap}>
					<table style={cs.table}>
						{/* Largeurs fixes → colonnes alignées sur toutes les cartes */}
						<colgroup>
							<col style={{ width: "6%" }} />
							<col style={{ width: "22%" }} />
							<col style={{ width: "13%" }} />
							<col style={{ width: "11%" }} />
							<col style={{ width: "11%" }} />
							<col style={{ width: "11%" }} />
							<col style={{ width: "11%" }} />
							<col style={{ width: "15%" }} />
						</colgroup>
						<thead>
							<tr>
								<th style={cs.th}>N°</th>
								<th style={cs.th}>Article</th>
								<th style={cs.th}>Numéro</th>
								<th style={cs.th}>Qté reçue</th>
								<th style={cs.th}>Qté restante</th>
								<th style={cs.thRight}>Prix achat</th>
								<th style={cs.thRight}>Prix vente</th>
								<th style={cs.thOutils}>Outils</th>
							</tr>
						</thead>
						<tbody>
							{articles.map((a, i) => (
								<tr
									key={`${a.id}-${i}`}
									style={{
										backgroundColor:
											i % 2 === 1
												? COLORS.bg
												: COLORS.surface,
									}}
								>
									<td style={cs.td} title={String(i + 1)}>
										{i + 1}
									</td>
									<td
										style={{ ...cs.td, fontWeight: 600 }}
										title={a.label}
									>
										{a.label}
									</td>
									<td style={cs.td} title={String(a.numero)}>
										{a.numero}
									</td>
									<td
										style={cs.td}
										title={String(a.quantite_recue)}
									>
										{a.quantite_recue}
									</td>
									<td
										style={cs.td}
										title={String(
											a.quantite_recue -
												a.quantite_utilisee,
										)}
									>
										{a.quantite_recue - a.quantite_utilisee}
									</td>
									<td
										style={cs.tdRight}
										title={fmt2(a.prix_achat_ht)}
									>
										{fmt2(a.prix_achat_ht)}
									</td>
									<td
										style={cs.tdRight}
										title={fmt2(a.prix_vente_ht)}
									>
										{fmt2(a.prix_vente_ht)}
									</td>
									<td style={cs.tdOutils}>
										<div style={cs.outils}>
											<button
												type="button"
												style={gs.iconBtn(
													COLORS.textMuted,
												)}
												onClick={() => onConso(i)}
												title="Consommer / rendre"
											>
												<FontAwesomeIcon
													icon={faWrench}
												/>
											</button>
											<button
												type="button"
												style={
													a.annulable
														? cs.iconDanger
														: {
																...cs.iconDanger,
																...cs.iconOff,
															}
												}
												disabled={!a.annulable}
												aria-label="Annuler la ligne"
												title={
													a.annulable
														? "Annuler la ligne"
														: a.raison_non_annulable
												}
												onClick={() =>
													setAnnulation({
														article: a,
													})
												}
											>
												<FontAwesomeIcon
													icon={faTimes}
												/>
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

const cs = {
	card: {
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
		marginBottom: 16,
		overflow: "hidden",
	},
	// Destructeur icône seule (repris de CommandeVirtuelleCard).
	iconDanger: {
		...gs.btnDanger,
		width: 32,
		height: 32,
		padding: 0,
		borderRadius: 8,
		fontSize: 14,
	},
	iconOff: {
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textLight,
		cursor: "not-allowed",
	},
	// Outils calés à droite, au même bord que les boutons de la commande
	// (CommandeHeader : padding 18 à droite, gap 8).
	outils: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 8,
	},
	tableWrap: { overflowX: "auto" },
	table: {
		width: "100%",
		minWidth: 760,
		tableLayout: "fixed",
		borderCollapse: "separate",
		borderSpacing: 0,
		fontSize: 13,
	},
	th: {
		padding: "10px 16px",
		textAlign: "left",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	td: {
		padding: "10px 16px",
		border: "none",
		textAlign: "left",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	thRight: {
		padding: "10px 16px",
		textAlign: "right",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	tdRight: {
		padding: "10px 16px",
		border: "none",
		textAlign: "right",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontVariantNumeric: "tabular-nums",
	},
}
cs.thOutils = { ...cs.thRight, paddingRight: 18 }
cs.tdOutils = { ...cs.td, paddingRight: 18 }

export default StockVirtuelCard
